// ДЕРЖАТЕЛЬ ЖИВОГО СТЕЙТА — конверт сессии и API чтения/записи из любого экрана.
//
// ВЫБОР МЕХАНИЗМА — CC (скоуп тикета «Каркас», пункт «держатель живого стейта»; стек заморожен:
// React 18.3.1 / Vite 5.4.10 / Tailwind 3.4.14 / Vitest 2.1.3). Взят контекст + `useState`, без
// стор-библиотеки. Причина простая: конверт — ОДИН объект, который целиком уходит в один ключ
// хранилища; обновляется он редко (уход из поля, смена шага), а читают его все экраны. Стор
// со срезами и подписками решает проблему перерисовок, которой здесь нет, и добавляет вторую
// форму правды поверх `s864.d1`.
//
// ПОЗИЦИЯ ЖИВЁТ В КОНВЕРТЕ, АДРЕС ЕЁ ОТРАЖАЕТ. Поэтому на монтировании адресная строка
// ПЕРЕПИСЫВАЕТСЯ под конверт (`router.start`, то есть `replaceState`), а не наоборот. Следствие
// названо прямо: прямая ссылка `#s5` на чистой сессии никуда не ведёт — человека вернёт на шаг
// из конверта. Так и задумано: на пятом шаге без ввода делать нечего, а хром §4 обещает возврат
// именно туда, где человека прервали.
//
// ⭐ ВХОД РАЗЛИЧАЕТСЯ АДРЕСОМ (`s895.d1`, слово Кирилла 23.09; B3-4). У правила выше есть ровно
// одно исключение, и оно про ВХОД, а не про позицию:
//   · адрес с хешем (`#s4/2/b`) — перезагрузка, и она возвращает на тот же шаг, как и обещает
//     хром §4. Здесь ничего не меняется;
//   · ГОЛЫЙ корень (`?view=wizard` без хеша) при непустом ключе — это лендинг в СОСТОЯНИИ 2:
//     тело экрана s1, счётчик показывает шаг остановки янтарём, в футере `continue` + ghost
//     `start over` (`s863.d1`). Адрес при этом не переписывается вовсе и остаётся голым.
// Два решения не спорят: они про разные события. До cc-130 голый корень переписывался под
// конверт безусловно, и состояние 2, ради которого CD рисовал второй кадр, было недостижимо.
//
// ПОЗИЦИЯ ОТ ЭТОГО ВТОРОЙ ИСТИНЫ НЕ ЗАВОДИТ. В состоянии 2 конверт по-прежнему помнит `s4/2/b`
// — человек просто стоит не там: он на входе. Что печатать, выводится из пары (`entry`,
// конверт) одним местом — полем `view` ниже, — а не хранится вторым полем.
//
// ТИХОЕ СОХРАНЕНИЕ ЖИВЁТ ЗДЕСЬ (cc-126, тикет `34a19875cbb881c3b015c19af3999a1c`). Хранилище
// умеет «записать/стереть/пробу» и не знает, когда это делать; политика — в этом файле:
//   · пишем на КАЖДУЮ смену конверта (ввод, смена шага, сброс), без дебаунса. Момент «на уходе
//     из поля» (`s866.d1`) держат ЭКРАНЫ — провайдер пишет то, что ему отдали; частота автосейва
//     решением не закрыта («за Кириллом», дописка `s864.d1`), здесь выбор CC;
//   · НЕ пишем конверт-на-старте: человек, открывший лендинг и ушедший, не должен оставлять
//     ключ. Первая же настоящая смена его заведёт;
//   · битый ключ и ключ чужой версии стираем и пишем причину в лог, экран молча показывает
//     состояние 1 (`s893.d1`);
//   · ключ стирает ТОЛЬКО подтверждённая отправка (`s889.d3`), не нажатие и не `start over`.
//
// ⭐ СУДЬЯ ЦИТАТ ЗОВЁТСЯ ЗДЕСЬ (строка 73, `s987.d3`, механика — `quotes.js`). Эффект: шаг ≥ 4 и в конверте нет
// результата под текущий вход → фоновый вызов, результат пишется в `quotes` (если вход не сменился, пока
// судья думал). Сабмит шага 6 ждёт тот же вызов через `awaitQuotes`; первая сборка PDF шага 7 — тоже
// (`s998.d3`). `judge` подменяется тестом.

import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { STEP_COUNT, bareAddress, formatAddress, parseAddress, stepCounter, stepNumber } from './address.js';
import { FIRST_STEP, REJECT, createSession, hasWork, startOver as resetSession } from './session.js';
import { footerFor } from './footerNav.js';
import { applyPalette, clearPalette } from './palette.js';
import { STORAGE_NOTICE, clearSession, probeStorage, readSession, writeSession } from './persist.js';
import { createRouter } from './router.js';
import { admitAddress } from './stepAdmission.js';
import { createQuotesRunner, failed, judgeStagesOf, quotesKey } from './quotes.js';
import { documentCarrierOf } from './documentPassport.js';

/** Ключ входа судьи для конверта или `null`, если конверт не собирается в ввод (этап без даты и т. п.). */
function quotesKeyOf(s) {
  try {
    const carrier = documentCarrierOf(s);
    return { key: quotesKey(judgeStagesOf(s), carrier), carrier };
  } catch {
    return null;
  }
}

/**
 * Конверт на старте: сохранённый, если под ключом лежит годный, иначе свежий.
 *
 * Причина отказа НЕ теряется: она понадобится тикету тихого сохранения (битый ключ и ключ чужой
 * версии удаляются, `s889.d4`), а оболочке — чтобы не делать вид, будто конверта не было вовсе.
 */
export function bootSession({ storage, clock } = {}) {
  const read = readSession(storage);
  if (read.session) return { session: read.session, restored: true, reason: null };
  return { session: createSession({ clock }), restored: false, reason: read.reason };
}

/**
 * ТРЕТИЙ ИСХОД НАЖАТИЯ НА CTA (B5-2): «экран увёл сам».
 *
 * До шага 3 исходов хватало двух: `false` — экран возразил (оболочка трясёт кнопку), всё
 * остальное — экран не возражает, и переход делает оболочка. Шагу 3 нужен третий: внутри шага
 * есть СВОИ переходы (панель A → панель B, карточка II → карточка III, последняя карточка →
 * финал), и ни один из них не «следующий шаг». Вернуть `false` значило бы трясти кнопку на
 * успешном ходе; вернуть `true` — уехать с шага 3 на шаг 4 мимо деки.
 *
 * ⚠️ СЕНТИНЕЛ, А НЕ ЕЩЁ ОДИН `boolean`. Третье значение у булева типа выражается только
 * `undefined`/`null`, а они уже заняты вызовами «экран ничего не сказал» — тем самым вторым
 * исходом. Имя стоит рядом с каналом, чтобы экраны брали его отсюда, а не писали литерал.
 */
export const HANDLED = 'handled';

const WizardContext = createContext(null);

/** Куда ведёт Begin лендинга при пустом конверте (`s956.d3` п.2). */
export const LANDING_STEP = 's2';

/** Конверт с допущенной позицией: шаг без данных → шаг 1, ввод не тронут (`s945.d3`). */
function admitStep(session, step) {
  const parsed = parseAddress(step);
  const admitted = admitAddress(session, parsed);
  return admitted === parsed ? session : { ...session, step: bareAddress(admitted) };
}

export function WizardProvider({ children, router: injectedRouter, storage, clock, judge }) {
  // Роутер поднимается ПЕРВЫМ: состояние входа ниже читает адресную строку, а читать её напрямую
  // через `window` провайдер не имеет права — окно принадлежит роутеру (в тестах оно подменено).
  const routerRef = useRef(null);
  if (routerRef.current === null) routerRef.current = injectedRouter ?? createRouter();
  const router = routerRef.current;

  const [boot] = useState(() => bootSession({ storage, clock }));
  // ⭐ ДОПУСК НА ПОДЪЁМЕ (`s945.d3`, вход 3 из 3). Конверт помнит шаг, для которого данных уже нет
  // (ключ пережил правку формы, ряды стёрты руками) — стартуем с шага 1, ВВОД НЕ ТРОГАЕМ: меняется
  // только позиция. Конверт-на-старте остаётся прежним объектом, поэтому поправленная позиция
  // уезжает в ключ первой же настоящей сменой, а не записью на монтировании.
  const [admitted] = useState(() => admitStep(boot.session, boot.session.step));

  /**
   * ⭐ ВХОД С ЛЕНДИНГА (`s956.d3`, тикет `/begin`). Кнопка Begin ведёт сразу на шаг 2 — но только
   * при ПУСТОМ конверте. Есть работа (`hasWork`, `s866.d1`) — сигнал ничего не меняет: голый адрес
   * лендинга поднимает обычное состояние входа ниже (шаг 1 с `continue` / `start over`, `s895.d1`),
   * п.7 решения. Шаг 2 в допуске не нуждается (`stepReady` пропускает шаги 1–2 всегда).
   *
   * ⚠️ СТАРТ НА ШАГЕ 2 В КЛЮЧ НЕ ПИШЕТСЯ — это всё ещё конверт-на-старте (см. автосейв): человек,
   * нажавший Begin и ушедший, не оставляет работы, иначе следующий Begin встретил бы его
   * `continue` над пустым визардом.
   */
  const [initial] = useState(() => (
    router.fromLanding?.() && !hasWork(admitted) ? { ...admitted, step: LANDING_STEP } : admitted
  ));
  const [session, setSession] = useState(initial);

  /**
   * СОСТОЯНИЕ ВХОДА (`s895.d1`): человек стоит на лендинге, а конверт помнит другое место.
   *
   * Поднимается ровно на одном сочетании — голый корень ПЛЮС непустой конверт. Голый корень с
   * пустым ключом состоянием входа не считается: показывать там нечего, кроме состояния 1, и
   * адрес честно переписывается в `#s1` (приёмка 3 «Каркаса» — вход это одна запись, и она
   * остаётся в силе).
   */
  // Считается по ДОПУЩЕННОМУ конверту, а не по ключу: ключ на `s3` без данных допуск ставит на
  // `s1`, и `hasWork` по ключу поднял бы состояние 2 над конвертом, в котором работы нет.
  // ⚠️ И ПО КОНВЕРТУ ДО ВХОДА С ЛЕНДИНГА: тот ставит `s2`, а `hasWork` считает шаг > 1 работой.
  const [entry, setEntry] = useState(() => router.hash() === '' && hasWork(admitted));
  const entryRef = useRef(entry);
  entryRef.current = entry;

  // Строка `s889.d5` — ОДИН раз за сессию. Флаг отдельно от значения: экран может строку убрать,
  // и после этого она не возвращается, сколько бы записей ни отказало дальше.
  const [storageNotice, setStorageNotice] = useState(null);
  const noticeRaised = useRef(false);
  const raiseNotice = useCallback(() => {
    if (noticeRaised.current) return;
    noticeRaised.current = true;
    setStorageNotice(STORAGE_NOTICE);
  }, []);
  const dismissNotice = useCallback(() => setStorageNotice(null), []);

  // Свежий конверт для колбэков: без него `next()` из старого замыкания увёл бы с шага, на
  // котором человек уже не стоит.
  const sessionRef = useRef(session);
  sessionRef.current = session;

  /**
   * ПЕРВАЯ ЗАПИСЬ АДРЕСА — LAYOUT-ЭФФЕКТ, И ЭТО НЕ ОПТИМИЗАЦИЯ, А ПОРЯДОК (B5-6).
   *
   * ⛔ ОБЫЧНЫЙ ЭФФЕКТ ЗАТИРАЛ ПОПРАВКУ ЭКРАНА. Пассивные эффекты React идут СНИЗУ ВВЕРХ: сперва
   * ребёнок, потом оболочка. Шаг 3 поправляет адрес, который состояние не подтверждает
   * (`deckRedirect`: панель B при закрытом гейте, финал по адресу удалённого этапа), и делал это
   * в своём эффекте — то есть ДО этой строки. Дальше `start` писал в строку адрес ЗАГРУЗОЧНОГО
   * конверта из замыкания первого рендера и возвращал ровно то, что экран только что убрал;
   * повторно поправка не срабатывала — её зависимость с тех пор не менялась. Наружу это выглядело
   * как «панель A нарисована, а в адресной строке `/b`»: конверт и строка разъехались.
   *
   * Layout-фаза родителя идёт ПЕРЕД пассивными эффектами детей, поэтому теперь порядок такой:
   * оболочка ставит адрес конверта → экран его поправляет → строка и конверт совпадают.
   *
   * ⚠️ ПОДПИСКА ОСТАЛАСЬ В ПАССИВНОМ ЭФФЕКТЕ. Ей порядок безразличен (`popstate` придёт не раньше
   * первого действия человека), а тащить её сюда значило бы делать подписку на окно в фазе,
   * которая блокирует отрисовку. `arm()` идемпотентен и зовётся обоими — `start` и `subscribe`.
   */
  useLayoutEffect(() => {
    // В состоянии входа адрес не трогаем вовсе (`s895.d1`) — см. шапку и `router.start`.
    router.start(session.step, { keepAddress: entry });
    // Подъём один на жизнь оболочки: роутер и конверт-на-старте не меняются.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const off = router.subscribe((address) => {
      if (!address) {
        // ⚠️ ДВЕ РАЗНЫЕ ПРИЧИНЫ «адреса нет», и путать их нельзя. Пустая строка — это ГОЛЫЙ
        // КОРЕНЬ: человек вернулся браузером на вход, и при непустом конверте там ждёт
        // состояние 2, а не шаг остановки. Всё остальное (`#s99/beta`) — опечатка в строке,
        // и её мы исправляем возвратом к конверту БЕЗ новой записи, иначе «назад» начнёт
        // ходить по чужим опечаткам.
        if (router.hash() === '' && hasWork(sessionRef.current)) {
          setEntry(true);
          return;
        }
        router.replace(sessionRef.current.step);
        return;
      }
      // Адрес назван — человек на конкретном шаге, а не на входе.
      setEntry(false);
      // ⭐ ДОПУСК НА `popstate` (`s945.d3`, вход 2 из 3): ручная правка хеша, «назад»/«вперёд».
      // Отказ ПОДМЕНЯЕТ запись, а не кладёт новую — иначе «назад» вёл бы на тот же негодный адрес.
      const admitted = admitAddress(sessionRef.current, address);
      if (admitted !== address) router.replace(admitted);
      setSession((s) => ({ ...s, step: bareAddress(admitted) }));
    });

    return () => {
      off();
      // Свой роутер снимаем целиком; чужой (подсунутый тестом или приложением) — не наш.
      if (!injectedRouter) router.stop();
    };
    // Подъём один на жизнь оболочки: роутер и конверт-на-старте не меняются.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Подъём хранилища: отвергнутое значение убираем, причину — в лог; заодно проверяем, даётся ли
  // запись вообще. Побочные действия живут в эффекте, а не в инициализаторе `useState`: тот в
  // StrictMode зовётся дважды, и стирание ключа поехало бы на каждый рендер-дубль.
  useEffect(() => {
    if (boot.reason === REJECT.CORRUPT || boot.reason === REJECT.VERSION) {
      // Человеку не печатаем ничего (`s893.d1`): строки «couldn't restore» в борде нет, а
      // выдумывать копи на экране, который просит доверия, дороже молчания. Причина — в лог,
      // чтобы первый живой прогон показал, что именно ломается.
      clearSession(storage);
      console.info(`[wizard] сохранённый конверт отклонён (${boot.reason}) — чистый старт`);
    }
    if (!probeStorage(storage).ok) raiseNotice();
    // Подъём один на жизнь оболочки.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Отправка подтверждена — дальше писать нечего: ключ стёрт намеренно, и автосейв не должен
  // воскресить его на первом же `popstate`.
  const submitted = useRef(false);


  /**
   * АВТОСЕЙВ. Пишем на каждую смену конверта — уход из поля (`update`), смена шага, сброс.
   *
   * Дебаунса нет намеренно, и это выбор CC, а не буква решения. `s866.d1` говорит «автосейв на
   * уходе из поля», но «уход из поля» — это про ЭКРАН: провайдер не видит полей, он видит конверт
   * и пишет всё, что ему отдали. Пока экранов с вводом нет, разницы не существует вовсе; когда
   * появятся — момент записи задаст их вызов `update`. Частота решением не закрыта: дописка
   * `s864.d1` оставила её «за Кириллом».
   *
   * Конверт-на-старте НЕ пишем: сравнение по ссылке, а не счётчиком рендеров — StrictMode
   * монтирует дважды, и счётчик на втором подъёме записал бы пустой конверт человеку, который
   * лендинг только открыл.
   *
   * Конверт-на-старте — это `initial`, а не сырой `boot.session`: позиция, поправленная допуском
   * (`s945.d3`) или входом с лендинга, уезжает в ключ первой же настоящей сменой, а не записью на
   * монтировании. До `/begin` сравнение шло с `boot.session`, и поправка допуска писалась сразу.
   */
  useEffect(() => {
    if (session === initial) return;
    if (submitted.current) return;
    if (!writeSession(session, storage).ok) raiseNotice();
  }, [session, initial, storage, raiseNotice]);

  /**
   * Названный переход. По умолчанию — НОВАЯ запись истории.
   *
   * ⭐ `{ replace: true }` ПРИЕХАЛО С B5-6, И ЭТО НЕ УДОБСТВО, А ЕДИНСТВЕННАЯ ЧЕСТНАЯ ФОРМА ДЛЯ
   * ПОПРАВКИ АДРЕСА. Шаг 3 чинит два адреса, которые грамматика разбирает, а состояние не
   * подтверждает: панель B/C при закрытом гейте и финал при недобранной последней карточке
   * (`deckRedirect`). Уведи там `pushState` — и кнопка «назад» браузера вернула бы человека
   * ровно на тот адрес, который экран только что поправил, а поправка сработала бы снова:
   * «назад» перестал бы работать вовсе. Подмена записи убирает неверный адрес из стека, как это
   * уже делают `back()` без своей истории и `start over`.
   */
  const goTo = useCallback((address, opts) => {
    // ⭐ ДОПУСК В ПЕРЕХОДЕ (`s922.d2` + `s945.d3`, вход 1 из 3). Конверт — из `sessionRef`: все
    // вызывающие пишут данные раньше нажатия (гейты шагов 2–3 проверяют уже записанное), поэтому
    // устаревшего снимка здесь не бывает. Негодный адрес (`null`) идёт дальше как был — его
    // громкий отказ живёт в роутере.
    const wanted = parseAddress(typeof address === 'string' ? address : formatAddress(address));
    const target = wanted ? admitAddress(sessionRef.current, wanted) : address;
    const parsed = opts && opts.replace ? router.replace(target) : router.go(target);
    // Любой названный переход снимает состояние входа: человек ушёл с лендинга на шаг.
    setEntry(false);
    setSession((s) => ({ ...s, step: bareAddress(parsed) }));
    return parsed;
  }, [router]);

  /** Следующий шаг. На последнем — `false`, а не молчаливый переход в никуда. */
  const next = useCallback(() => {
    const n = stepNumber(sessionRef.current.step);
    if (n >= STEP_COUNT) return false;
    goTo({ step: n + 1 });
    return true;
  }, [goTo]);

  /**
   * `continue →` состояния 2 (`s1.resume.cta`): увести с лендинга на шаг, где человека прервали.
   *
   * Переход — НОВАЯ запись в истории (`goTo`, то есть `pushState`), а не подмена: голый корень
   * остаётся позади, и браузерный «назад» с шага возвращает на лендинг, а не уносит с сайта.
   *
   * ⚠️ КОНВЕРТ МОЖЕТ ПОМНИТЬ САМ ПЕРВЫЙ ШАГ. `hasWork` — это `step > 1 ИЛИ непустой ввод`
   * (`s866.d1`), поэтому состояние 2 достижимо и на `s1`: человек начал заполнять шаг 1 и
   * вернулся браузером на вход. Вести в этом случае некуда, и `continue` работает как `begin` —
   * шаг вперёд. Кнопка, которая ничего не делает, хуже любой из двух трактовок.
   */
  const resume = useCallback(() => {
    setEntry(false);
    const target = sessionRef.current.step;
    if (stepNumber(target) <= 1) return next();
    goTo(target);
    return true;
  }, [goTo, next]);

  /**
   * Назад. Сначала пробуем историю — тогда «назад» в футере и «назад» браузера это одно и то же
   * действие, и стек не растёт. Если своих записей в истории нет (человек вернулся по
   * сохранённому конверту прямо на шаг 4 — приходить ему было неоткуда), отступаем подменой
   * записи: `history.back()` там унёс бы с сайта.
   */
  const back = useCallback(() => {
    if (router.back()) return true;

    const n = stepNumber(sessionRef.current.step);
    if (n <= 1) return false;
    const parsed = router.replace({ step: n - 1 });
    setSession((s) => ({ ...s, step: bareAddress(parsed) }));
    return true;
  }, [router]);

  /**
   * СУДЬЯ ЦИТАТ (`s987.d3`). `runQuotesFor` — результат под вход конверта `s`: записанный, если ключ совпал,
   * иначе вызов бегунка (один на ключ). Пишет в конверт только если вход за время вызова не сменился. Никогда
   * не бросает: сбой — `null`, документ просто без цитат.
   */
  const runner = useMemo(() => createQuotesRunner(judge ? { judge } : {}), [judge]);
  const runQuotesFor = useCallback((s, { retry = false } = {}) => {
    const at = quotesKeyOf(s);
    if (!at) return Promise.resolve(null);
    if (!retry && s.quotes && s.quotes.key === at.key) return Promise.resolve(s.quotes);
    return runner.run(s, at.carrier, { retry }).then((res) => {
      setSession((cur) => {
        if (cur.quotes === res) return cur;
        const now = quotesKeyOf(cur);
        return now && now.key === res.key ? { ...cur, quotes: res } : cur;
      });
      return res;
    }, () => null);
  }, [runner]);

  useEffect(() => {
    if (stepNumber(session.step) < 4) return;
    runQuotesFor(session);
  }, [session, runQuotesFor]);

  /**
   * Сабмит ждёт судью (п. 3 `s987.d3`). Записанный сбой транспорта получает ОДИН второй шанс здесь — на
   * нажатии ожидание оправдано. Возвращает `{key, system2}` или `null`.
   *
   * `retryFailed: false` — шаг 7 (`s998.d3`): ждать вызов под текущий вход, но записанный сбой не повторять —
   * второй шанс уже был на CTA шага 6, а на старом конверте сбой записан под тот же вход и так.
   */
  const awaitQuotes = useCallback(({ retryFailed = true } = {}) => {
    const s = sessionRef.current;
    const at = quotesKeyOf(s);
    const stale = retryFailed && at && s.quotes && s.quotes.key === at.key && failed(s.quotes);
    return runQuotesFor(s, { retry: Boolean(stale) });
  }, [runQuotesFor]);

  /** Запись конверта из любого экрана. Обновление — функция от прежнего конверта. */
  const update = useCallback((updater) => {
    setSession((prev) => {
      const nextSession = typeof updater === 'function' ? updater(prev) : updater;
      if (!nextSession || typeof nextSession !== 'object') {
        throw new Error('обновление конверта обязано вернуть конверт');
      }
      return nextSession;
    });
  }, []);

  /**
   * `start over` (`s866.d1`, слот `s1.resume.restart`): сброс конверта + возврат на шаг 1
   * подменой записи. Сразу, без подтверждения и без undo — названная цена MVP (пункт 10 борда).
   *
   * Адрес становится `#s1`, а не остаётся голым корнем: сбрасывать больше нечего, и состояние 2
   * на этом ключе уже не поднимется — `hasWork` у сброшенного конверта ложь. Ключ при этом НЕ
   * стирается: сброшенный конверт пишет автосейв, `sessionId` переживает сброс.
   */
  const startOver = useCallback((opts) => {
    const fresh = resetSession(sessionRef.current, opts);
    setEntry(false);
    router.replace(fresh.step);
    setSession(fresh);
    return fresh;
  }, [router]);

  /**
   * Исход отправки (`s889.d3`). Ключ стирает ТОЛЬКО подтверждение сервера, а не нажатие кнопки:
   * при сбое отправки работа обязана остаться на устройстве.
   *
   * ⭐ ФОРМА — `{ sent }` (`s921.d1`, B9-4), та же, что возвращает транспорт `sendPassport`
   * (`src/send/transport.js`): `sent: true` — ключ стёрт и автосейв выключен (`submitted`);
   * `sent: false` — с ключом не делается ничего. До B9-4 поле звалось `receipt` (форма CC, ждала
   * ратификации); переименовано решением, старое имя не читается — вызывающих вне тестов не было.
   *
   * Снятый §VI (судья цитат не дал двух карточек) — это успех, а не частичный провал: слепок
   * создан, и ключ стирается. Дом долга целиком — `docs/design/submit-seam.md`, механизм 3.
   *
   * @param {{sent?: boolean}} outcome
   * @returns {boolean} стёрли ли ключ
   */
  const settleSubmit = useCallback((outcome = {}) => {
    if (outcome.sent !== true) return false;
    submitted.current = true;
    clearSession(storage);
    return true;
  }, [storage]);

  /**
   * ГЕЙТ ЭКРАНА (B4-4, `s461`): кнопка стоит в футере, а правило — у шага.
   *
   * ⚠️ ПОЧЕМУ КАНАЛ, А НЕ СТРОКА В ТАБЛИЦЕ ФУТЕРА. `footerNav.js` отвечает на вопрос «какая пара
   * стоит внизу» и прямо оговаривает, что блокировка CTA — по спеке шага, а не его предмет. Но
   * и экран нарисовать эту кнопку не может: она в чужой полосе, и вторая такая же означала бы
   * вторую правду о хроме. Отсюда канал: экран ОБЪЯВЛЯЕТ состояние гейта, оболочка его печатает.
   *
   * ⚠️ ОБРАБОТЧИК ЖИВЁТ В `ref`, А ОБЪЯВЛЕНИЕ — В СТЕЙТЕ, и разделены они не для красоты.
   * Замыкание `onPress` пересоздаётся на каждом рендере экрана; держи его стейт — публикация
   * меняла бы состояние на каждом рендере и звала бы следующий, то есть цикл. В `ref` он
   * обновляется молча и всегда свежий, а перерисовку вызывает только настоящая смена НАДПИСИ.
   *
   * ⭐ С B5-2 КАНАЛ НЕСЁТ И `back` — ТУ ЖЕ ПАРУ «НАДПИСЬ + ОБРАБОТЧИК», что и CTA. Причина
   * ровно та, по которой канал заводился для CTA: у шага 3 надписей возврата ЧЕТЫРЕ, и все
   * ведут ВНУТРЬ шага (панель к панели, карточка к карточке, карточка I к интро). Оболочка про
   * внутренности шага не знает, а таблица `footerNav.js` по устройству выражает только «какая
   * пара стоит внизу». Оба поля НЕОБЯЗАТЕЛЬНЫ: не объявил — печатается и работает то, что было
   * до B5-2, и шаг 2 этого расширения не замечает вовсе.
   */
  const [gate, setGate] = useState(null);
  const gatePress = useRef(null);
  const gateBack = useRef(null);

  const publishGate = useCallback((spec) => {
    gatePress.current = spec && typeof spec.onPress === 'function' ? spec.onPress : null;
    gateBack.current = spec && typeof spec.onBack === 'function' ? spec.onBack : null;
    setGate((prev) => {
      if (!spec) return prev === null ? prev : null;
      const value = {
        kind: spec.kind ?? 'block',
        label: spec.label ?? null,
        count: spec.count ?? null,
        lead: spec.lead ?? null,
        trail: spec.trail ?? null,
        blocked: !!spec.blocked,
        backLabel: spec.backLabel ?? null,
        // ⭐ ВИД КНОПКИ (B7-3): `solid` — amber solid шага 5 (`s456.d3`). Не объявлен — вид
        // таблицы (outline по борду шага 1). Батч по другим экранам не делается.
        tone: spec.tone ?? null,
        // ⭐ ПРИГЛУШЁННАЯ CTA (B7-4, `s952.d2` п. 6 по образцу `s493.d3`): видна и нажимается, но
        // приглушена, пока экран доигрывает reveal. Не `blocked`: закрытая кнопка трясётся и
        // никуда не ведёт, а эта ведёт.
        muted: !!spec.muted,
        // ⭐ ВЫХОД ИЗ ПРИГЛУШЕНИЯ ПЛАВНО (B6-7, `s977.d2` п. 3): только естественная посадка reveal
        // (конец кадров или страховка). skip, тап, повторный вход — без перехода. Ставит класс
        // оболочка (`WizardShell.jsx`), экран только объявляет, какой это был выход.
        fade: !!spec.fade,
      };
      // Сравнение ПО ЗНАЧЕНИЮ: экран публикует на каждом рендере, и возврат прежнего объекта —
      // единственное, что отличает «объявил то же самое» от «сменил состояние».
      if (prev
        && prev.kind === value.kind && prev.label === value.label && prev.count === value.count
        && prev.lead === value.lead && prev.trail === value.trail && prev.blocked === value.blocked
        && prev.backLabel === value.backLabel && prev.tone === value.tone && prev.muted === value.muted
        && prev.fade === value.fade) {
        return prev;
      }
      return value;
    });
  }, []);

  /**
   * ТРЯСКА CTA ПО ПРОСЬБЕ ЭКРАНА (B5-3) — третье и последнее расширение канала гейта.
   *
   * ⭐ ЗАЧЕМ ОНО ПОНАДОБИЛОСЬ. До B5-3 отказ гейта всегда начинался с нажатия на саму кнопку, и
   * оболочка трясла её в ответ на свой же `pressCta() === false`. У деки отказ начинается ещё в
   * ОДНОМ месте: тап по запертой точке панели B или C (борд `:524`, `nudge()` `:565`) обязан
   * тряхнуть ту же кнопку внизу — иначе человек, который ткнул в точку, не получает ответа вовсе.
   * Точка живёт в полосе шага, кнопка — в подвале оболочки, и дотянуться до узла экран не может.
   *
   * ФОРМА — СЧЁТЧИК, А НЕ ФЛАГ. Два отказа подряд обязаны дать две тряски; булев `true→true`
   * второго эффекта не вызвал бы, и второй тап промолчал бы. Сама анимация по-прежнему живёт в
   * оболочке (`classList` + reflow, чтобы не отнимать фокус) — сюда переехал только ПОВОД.
   */
  const [ctaShake, setCtaShake] = useState(0);
  const shakeCta = useCallback(() => setCtaShake((n) => n + 1), []);

  /**
   * Нажатие на CTA. Сперва спрашиваем экран, потом идём.
   *
   * Экран отвечает `false` = «перехватил»: гейт закрыт (трясти черновик, мигнуть `add`) или
   * вопрос вместо перехода (шит-нудж). Всё остальное — обычный ход, и он тот же, что был до
   * канала: на входе `resume`, на шаге `next`.
   */
  const pressCta = useCallback(() => {
    const ask = gatePress.current;
    if (!ask) return entryRef.current ? resume() : next();
    const answer = ask();
    if (answer === false) return false;
    // Экран увёл сам — оболочке делать нечего, и трясти тоже нечего.
    if (answer === HANDLED) return true;
    return entryRef.current ? resume() : next();
  }, [next, resume]);

  /**
   * Нажатие на `back`. Симметрично `pressCta`: сперва спрашиваем экран, потом идём сами.
   *
   * Экран отвечает `false` = «перехватил»: у шага 3 возврат почти всегда ведёт ВНУТРЬ шага
   * (панель B → панель A, карточка II → карточка I, карточка I → интро), и только с интро он
   * уходит на предыдущий шаг — там экран не перехватывает и работает обычный `back`.
   */
  const pressBack = useCallback(() => {
    const ask = gateBack.current;
    if (ask && ask() === false) return false;
    return back();
  }, [back]);

  /**
   * ТОСТ ПЕРЕХОДА (`s2.toast.next`, `s855.d1`) — единственная строка визарда, которая обязана
   * пережить смену экрана, и поэтому единственная, которую печатает оболочка.
   *
   * ⚠️ ТАЙМЕР ЗДЕСЬ ЕСТЬ, И ЭТО НЕ ОТСТУПЛЕНИЕ ОТ `s534.d4`. То решение сняло таймеры с фактов о
   * СПИСКЕ (лейбл подтянутого конца, тост удаления): у них есть «следующее действие человека»,
   * которое их снимает, и у тоста удаления вдобавок кнопка, которую нельзя отнять по часам. У
   * прощальной строки следующего действия нет вовсе — человек уже на другом шаге, — и снять её
   * больше нечем. Число берёт тот, кто зовёт: у борда шага 2 это 1600мс (`TOAST_MS`).
   */
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const announce = useCallback((node, ms) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast(node);
    toastTimer.current = setTimeout(() => {
      toastTimer.current = null;
      setToast(null);
    }, ms);
  }, []);
  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);

  // АДРЕС ПЕЧАТАЕМОГО ЭКРАНА — единственное место, где пара (`entry`, конверт) сводится в
  // ответ «что на экране». В состоянии входа это лендинг, во всех прочих — шаг из конверта.
  // Счётчик при этом ВСЕГДА идёт от конверта: в состоянии 2 он обязан говорить, где человек
  // остановился (`03 / 07` борда), иначе шапка и кнопка разойдутся в числах.
  const view = entry ? FIRST_STEP : session.step;

  /**
   * ТЕМПЕРАТУРА ПАЛИТРЫ (`s903.d1`): тёплый набор на шагах 3–5, холодный везде ещё.
   *
   * ⚠️ ЧИТАЕТСЯ `view`, А НЕ `session.step`, И ЭТО БУКВА РЕШЕНИЯ. В состоянии входа конверт
   * помнит, скажем, `s4/2/b`, а на экране лендинг — по `session.step` лендинг покрасился бы
   * тёплым. Правило живёт в `palette.js`, здесь только момент применения.
   *
   * ⚠️ СНЯТИЕ НА РАЗМОНТИРОВАНИИ — ОТДЕЛЬНЫМ ЭФФЕКТОМ, потому что причина у него другая:
   * документ переживает оболочку (дев-маршрут паспорта в той же вкладке), а паспорт читает пять
   * из восьми имён раскола. Забытый атрибут двинул бы byte-parity секций молча.
   */
  useEffect(() => {
    if (typeof document === 'undefined') return;
    applyPalette(document.documentElement, view);
  }, [view]);

  useEffect(() => () => {
    if (typeof document !== 'undefined') clearPalette(document.documentElement);
  }, []);

  const value = useMemo(() => {
    const work = hasWork(session);

    return {
      session,
      entry,
      view,
      address: parseAddress(session.step),
      counter: stepCounter(session.step),
      footer: footerFor(view, { hasWork: work }),
      gate,
      publishGate,
      ctaShake,
      shakeCta,
      pressCta,
      pressBack,
      toast,
      announce,
      hasWork: work,
      restored: boot.restored,
      rejectReason: boot.reason,
      storageNotice,
      dismissNotice,
      router,
      goTo,
      next,
      back,
      resume,
      update,
      startOver,
      settleSubmit,
      awaitQuotes,
    };
  }, [
    session, entry, view, boot.restored, boot.reason, storageNotice, dismissNotice,
    gate, publishGate, ctaShake, shakeCta, pressCta, pressBack, toast, announce,
    router, goTo, next, back, resume, update, startOver, settleSubmit, awaitQuotes,
  ]);

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
}

/** Доступ к конверту и навигации. Вне провайдера — внятная ошибка, а не `undefined.session`. */
export function useWizard() {
  const value = useContext(WizardContext);
  if (!value) throw new Error('useWizard вызван вне <WizardProvider>');
  return value;
}
